import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { H3, P } from '@/components/ui/typography';
import { themeColorLinkClassNames } from '@/components/ui/utils';
import { cn } from '@/lib/utils';
import type { SpotlightCardModel } from '@/components/Spotlight/spotlight';
import 'material-symbols';

const CARD_SURFACE = cn(
    'flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl border px-6 pt-6 pb-6 text-center shadow-sm',
    'light:bg-gz-surface-light light:border-gz-neutral-200',
    'dark:bg-gz-blue-700 dark:border-gz-blue-550',
);

const CARD_HOVER = cn(
    'transition-[background-color,transform] duration-200 ease-out',
    'motion-safe:hover:-translate-y-px',
    'light:hover:bg-gz-tan-250',
    'dark:hover:bg-gz-blue-550',
);

const LABEL_BADGE = cn(
    'rounded-full border px-2 py-0.5 font-normal',
    'bg-gz-gold-100 border-gz-gold-700 text-gz-neutral-900',
    'dark:bg-gz-blue-800 dark:border-gz-blue-600 dark:text-gz-blue-50',
);

/**
 * Neighbor slides are `scale-90` (0.9). Width is that same factor, then the copy is scaled
 * by the inverse, so each type style renders at its own size instead of shrinking with the card.
 * Landing also uses `lg:scale-[0.82]`, so it overrides both at `lg`.
 */
export const HUB_SPOTLIGHT_NEIGHBOR_COPY_CLASS = 'origin-center w-[90%] scale-[1.111111]';
export const LANDING_SPOTLIGHT_NEIGHBOR_COPY_CLASS = 'origin-center w-[90%] scale-[1.111111] lg:w-[82%] lg:scale-[1.219512]';

/** Where this card is shown. Each surface gets its own Heap click name. */
export type SpotlightSurface = 'landing' | 'hub-sheet';

const SPOTLIGHT_CARD_CLICK: Record<SpotlightSurface, string> = {
    'landing': 'Landing Spotlight Card Clicked',
    'hub-sheet': 'Hub Spotlight Card Clicked',
};

export interface SpotlightCardProps {
    card: SpotlightCardModel;
    /** Which carousel rendered the card. Landing and the hub sheet are tracked separately. */
    surface: SpotlightSurface;
    /** The centered slide. Neighbors use the smaller badge, title, and description styles. */
    focused?: boolean;
    /** Set on a scaled-down neighbor slide so the copy renders at its own type size. */
    copyClassName?: string;
}

const isAppPath = (url: string) => url.startsWith('/') && !url.startsWith('//');

const spotlightTrackingProps = (card: SpotlightCardModel, surface: SpotlightSurface) => ({
    'data-track-click': SPOTLIGHT_CARD_CLICK[surface],
    'data-track-location': surface,
    'data-track-spotlight-slug': card.slug,
    'data-track-url': card.url,
    'data-track-title': card.title ?? '',
    'data-track-external': String(card.external),
});

export const SpotlightCard = ({
    card,
    surface,
    focused = true,
    copyClassName,
}: SpotlightCardProps) => {
    const showLabel = card.imageLayout === 'none' && Boolean(card.label);
    const showTitle = Boolean(card.title) && card.imageLayout !== 'replaces-title';
    const showDescription = Boolean(card.description);
    const showLink = Boolean(card.clickable && card.caption);
    const titleVariant = focused ? 'type-body-large-bold' : 'type-body-bold';
    const descriptionVariant = focused ? 'type-body-large' : 'type-caption';

    const body = (
        <>
            {card.image && card.imageLayout !== 'none' && (
                <div
                    className={cn(
                        'flex items-center justify-center',
                        card.imageLayout === 'replaces-title' ? 'w-full' : 'max-w-[12rem]',
                        'dark:rounded-md dark:bg-white dark:px-3 dark:py-2',
                    )}
                >
                    <img
                        src={card.image.url}
                        alt={card.imageLayout === 'replaces-title'
                            ? (card.image.alternativeText || card.title || 'Spotlight')
                            : ''}
                        className={cn(
                            'object-contain',
                            card.imageLayout === 'replaces-title' ? 'max-h-28 w-full' : 'max-h-10 w-auto',
                        )}
                    />
                </div>
            )}
            {(showLabel || showTitle || showDescription || showLink) && (
                <div className={cn('flex flex-col items-center gap-2.5', copyClassName ?? 'w-full')}>
                    {showLabel && (
                        <Badge className={cn(LABEL_BADGE, focused ? 'type-body-default' : 'type-caption')}>{card.label}</Badge>
                    )}
                    {(showTitle || showDescription || showLink) && (
                        <div className="flex w-full flex-col items-center gap-2">
                            {(showTitle || showDescription) && (
                                <div className="flex w-full flex-col items-center gap-0.5">
                                    {showTitle && (
                                        <H3 variant={titleVariant} className={cn(focused && 'spotlight-focused-title', 'text-gz-neutral-900 dark:text-gz-neutral-100')}>
                                            {card.title}
                                        </H3>
                                    )}
                                    {showDescription && (
                                        <P variant={descriptionVariant} className="spotlight-description light:text-gz-neutral-900 dark:text-gz-neutral-200">
                                            {card.description}
                                        </P>
                                    )}
                                </div>
                            )}
                            {showLink && (
                                <span className={cn(
                                    'inline-flex items-center gap-1 border-b border-current pb-px type-caption font-normal',
                                    themeColorLinkClassNames,
                                )}
                                >
                                    {card.caption}
                                    <span className="material-symbols-rounded text-base leading-none" aria-hidden>
                                        {card.external && !card.captionIsFallback ? 'open_in_new' : 'arrow_forward'}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );

    if (!card.clickable || !card.url) {
        return <article className={CARD_SURFACE}>{body}</article>;
    }

    const linkClass = cn(
        CARD_SURFACE,
        CARD_HOVER,
        'focus-visible:outline-2 focus-visible:outline-offset-2',
        'light:focus-visible:outline-gz-marine-700 dark:focus-visible:outline-white',
    );

    if (card.external) {
        return (
            <a
                href={card.url}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
                {...spotlightTrackingProps(card, surface)}
            >
                {body}
            </a>
        );
    }

    if (isAppPath(card.url)) {
        return (
            <Link to={card.url} className={linkClass} {...spotlightTrackingProps(card, surface)}>
                {body}
            </Link>
        );
    }

    return (
        <a href={card.url} className={linkClass} {...spotlightTrackingProps(card, surface)}>
            {body}
        </a>
    );
};
